import { useState, useEffect } from 'react'
import { ChevronDownIcon, Loader2 } from 'lucide-react'
import { Button } from './button'
import { Input } from './input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from './dropdown-menu'
import { clientsApi, ClientData } from '@/lib/api'

interface ClientDropdownProps {
  value?: string
  onValueChange?: (value: string) => void
  selectedClients?: string[]
  onClientsChange?: (clientIds: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  multiple?: boolean
}

export function ClientDropdown({
  value,
  onValueChange,
  selectedClients,
  onClientsChange,
  placeholder = "Select client",
  className,
  disabled = false,
  multiple = true
}: ClientDropdownProps) {
  // Support both single (value/onValueChange) and multiple (selectedClients/onClientsChange) modes
  const effectiveSelectedClients = multiple 
    ? (selectedClients || []) 
    : (value ? [value] : []);
  
  const effectiveOnChange = multiple 
    ? (onClientsChange || (() => {}))
    : ((clientIds: string[]) => onValueChange?.(clientIds[0] || ''));
  const [clients, setClients] = useState<ClientData[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [clientSearchQuery, setClientSearchQuery] = useState("")

  // Filter clients based on search query
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(clientSearchQuery.toLowerCase())
  )

  // Load clients
  const fetchClients = async () => {
    setLoadingClients(true)
    try {
      const response = await clientsApi.getClients(undefined, 1000, 0, 'active')
      setClients(response.clients)
    } catch (error) {
      console.error('Error fetching clients:', error)
    } finally {
      setLoadingClients(false)
    }
  }

  // Load clients on component mount
  useEffect(() => {
    fetchClients()
  }, [])

  const handleClientChange = (clientId: string, checked: boolean) => {
    if (multiple) {
      if (checked) {
        effectiveOnChange([...effectiveSelectedClients, clientId])
      } else {
        effectiveOnChange(effectiveSelectedClients.filter(id => id !== clientId))
      }
    } else {
      effectiveOnChange(checked ? [clientId] : [])
    }
  }

  const getDisplayText = () => {
    if (effectiveSelectedClients.length === 0) {
      return placeholder
    }
    
    if (multiple) {
      const selectedClientNames = effectiveSelectedClients
        .map(id => clients.find(c => c.id === id)?.name)
        .filter(Boolean)
      
      if (selectedClientNames.length === 0) {
        return placeholder
      }
      
      if (selectedClientNames.length === 1) {
        return selectedClientNames[0]
      }
      
      return `${selectedClientNames.length} clients selected`
    } else {
      const selectedClient = clients.find(c => c.id === effectiveSelectedClients[0])
      return selectedClient?.name || placeholder
    }
  }

  if (loadingClients) {
    return (
      <div className={`flex items-center justify-center py-2 ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        <span className="text-sm">Loading clients...</span>
      </div>
    )
  }

  return (
    <DropdownMenu onOpenChange={(open) => {
      if (!open) {
        setClientSearchQuery("")
      }
    }}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className={`flex-1 justify-between ${className}`}
          disabled={disabled}
        >
          {getDisplayText()}
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56 p-0">
        {/* Search input */}
        <div className="p-2 border-b">
          <Input
            placeholder="Search clients..."
            value={clientSearchQuery}
            onChange={(e) => setClientSearchQuery(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
        </div>

        {/* Client list */}
        <div className="max-h-48 overflow-y-auto">
          {clients.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No clients available
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No clients found for "{clientSearchQuery}"
            </div>
          ) : (
            filteredClients.map((client) => (
              <DropdownMenuCheckboxItem
                key={client.id}
                checked={effectiveSelectedClients.includes(client.id)}
                onCheckedChange={(checked) => handleClientChange(client.id, checked)}
                className="mx-1 rounded-sm"
              >
                {client.name}
              </DropdownMenuCheckboxItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
